/**
 * CustomerInfoPaymentView — pure presentational "Your details" / payment UI.
 *
 * Shared by:
 *   - CustomerInfoPaymentStep (production: real form, Stripe, Turnstile)
 *   - PreviewCheckoutStep (draft preview: inert mock state)
 *
 * Visual geometry lives HERE only. Do not re-encode Field / PaymentTile /
 * summary card styling in adapters.
 *
 * Matched against design-imports/screens.jsx DetailsStep + FlowSummary.
 */

import * as React from 'react';
import { formatDuration } from '@/components/booking-flow/FlowSteps';
import { FLOW_DEFAULTS } from '@/components/booking-flow/themes';
import { SectionLabel } from '@/components/booking-flow/FlowScaffold';
import { PolicyCard } from './PolicyCard';

const INK = 'var(--f-ink, #1c160f)';
const INK_SOFT = 'var(--f-ink-soft, rgba(28,18,12,0.62))';
const INK_FAINT = 'var(--f-ink-faint, rgba(28,18,12,0.40))';
const HAIRLINE = 'var(--f-hairline, rgba(28,18,12,0.10))';
const SURFACE = 'var(--f-surface, #FBF8F2)';
const CARD_SHADOW = FLOW_DEFAULTS.cardShadow;

// ─── Primitives ──────────────────────────────────────────────────

export function DetailsField({
  label,
  value,
  onChange,
  onBlur,
  placeholder,
  type = 'text',
  inputMode,
  autoComplete,
  maxLength,
  error,
  multiline,
  name,
  readOnly = false,
}: {
  label: React.ReactNode;
  value: string;
  onChange?: (v: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  type?: string;
  inputMode?: 'numeric' | 'tel' | 'email' | 'text';
  autoComplete?: string;
  maxLength?: number;
  error?: string;
  multiline?: boolean;
  name?: string;
  readOnly?: boolean;
}) {
  const [focus, setFocus] = React.useState(false);
  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 0',
    background: 'transparent',
    border: 'none',
    borderBottom: `1px solid ${error ? 'rgba(200,60,40,0.55)' : focus ? INK_SOFT : HAIRLINE}`,
    borderRadius: 0,
    fontFamily: 'inherit',
    fontSize: 15,
    color: INK,
    outline: 'none',
    letterSpacing: '-0.005em',
    transition: 'border-color 200ms ease',
    resize: 'none' as const,
  };

  return (
    <label style={{ display: 'block', position: 'relative' }}>
      <div
        style={{
          fontSize: 10,
          textTransform: 'uppercase' as const,
          letterSpacing: '0.18em',
          fontWeight: 600,
          color: INK_FAINT,
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      {multiline ? (
        <textarea
          name={name}
          value={value}
          onChange={readOnly ? undefined : (e) => onChange?.(e.target.value)}
          onFocus={() => !readOnly && setFocus(true)}
          onBlur={() => {
            setFocus(false);
            onBlur?.();
          }}
          placeholder={placeholder}
          maxLength={maxLength}
          rows={2}
          readOnly={readOnly}
          tabIndex={readOnly ? -1 : undefined}
          aria-readonly={readOnly || undefined}
          style={inputStyle}
        />
      ) : (
        <input
          name={name}
          value={value}
          onChange={readOnly ? undefined : (e) => onChange?.(e.target.value)}
          onFocus={() => !readOnly && setFocus(true)}
          onBlur={() => {
            setFocus(false);
            onBlur?.();
          }}
          placeholder={placeholder}
          type={type}
          inputMode={inputMode}
          autoComplete={autoComplete}
          maxLength={maxLength}
          readOnly={readOnly}
          tabIndex={readOnly ? -1 : undefined}
          aria-readonly={readOnly || undefined}
          style={inputStyle}
        />
      )}
      {error && (
        <div
          style={{
            marginTop: 5,
            fontSize: 11,
            lineHeight: 1.4,
            color: 'rgba(200,60,40,0.9)',
            letterSpacing: '-0.005em',
          }}
        >
          {error}
        </div>
      )}
    </label>
  );
}

export function PaymentTile({
  selected,
  onClick,
  label,
  sub,
  icon,
  readOnly = false,
}: {
  selected: boolean;
  onClick?: () => void;
  label: string;
  sub: string;
  icon: React.ReactNode;
  readOnly?: boolean;
}) {
  const style: React.CSSProperties = {
    textAlign: 'left',
    background: selected ? 'var(--f-accent-soft, rgba(0,0,0,0.04))' : SURFACE,
    border: `0.5px solid ${selected ? 'var(--f-accent-border, rgba(28,18,12,0.20))' : HAIRLINE}`,
    boxShadow: selected
      ? `0 0 0 1px var(--f-accent-border, rgba(28,18,12,0.20)), ${CARD_SHADOW}`
      : CARD_SHADOW,
    padding: '14px 14px 16px',
    borderRadius: 14,
    cursor: readOnly ? 'default' : 'pointer',
    fontFamily: 'inherit',
    color: INK,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    transition: 'background 240ms ease, border-color 240ms ease, box-shadow 240ms ease',
    WebkitTapHighlightColor: 'transparent',
  };

  const body = (
    <>
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          background: selected ? 'var(--f-accent, #1c160f)' : 'rgba(28,18,12,0.06)',
          color: selected ? 'var(--f-sel-fg, #F5EDE0)' : INK,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background 240ms ease, color 240ms ease',
        }}
      >
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 13.5, fontWeight: 500, color: INK, letterSpacing: '-0.005em' }}>
          {label}
        </div>
        <div style={{ fontSize: 11.5, color: INK_SOFT, marginTop: 2, letterSpacing: '-0.005em' }}>
          {sub}
        </div>
      </div>
    </>
  );

  if (readOnly) {
    // Static status display — not a toggle. Avoid aria-pressed on a non-control.
    return (
      <div
        role="group"
        aria-label={`${label}${selected ? ', selected' : ''}`}
        style={style}
      >
        {body}
      </div>
    );
  }

  return (
    <button type="button" onClick={onClick} aria-pressed={selected} style={style}>
      {body}
    </button>
  );
}

export function CardIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <rect x="1.5" y="3" width="11" height="8" rx="1.3" stroke="currentColor" strokeWidth="1.2" />
      <path d="M1.5 5.8h11" stroke="currentColor" strokeWidth="1.2" />
      <path d="M3.5 8.5h2.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

export function WalletIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path
        d="M1.5 4.5C1.5 3.4 2.4 2.5 3.5 2.5h7c1.1 0 2 .9 2 2v6c0 1.1-.9 2-2 2h-7c-1.1 0-2-.9-2-2v-6z"
        stroke="currentColor"
        strokeWidth="1.2"
      />
      <circle cx="10" cy="7.5" r="1" fill="currentColor" />
    </svg>
  );
}

export function ServicePriceSummary({
  serviceName,
  duration,
  price,
  tipAmount = 0,
}: {
  serviceName: string;
  duration: number;
  price: number;
  tipAmount?: number;
}) {
  return (
    <div
      style={{
        marginTop: 24,
        background: SURFACE,
        border: `0.5px solid ${HAIRLINE}`,
        borderRadius: 18,
        padding: '18px 18px',
        boxShadow: CARD_SHADOW,
      }}
    >
      <div
        style={{
          fontFamily: 'var(--f-display, "Inter Tight", system-ui, sans-serif)',
          fontStyle: 'var(--f-display-style, normal)' as React.CSSProperties['fontStyle'],
          fontWeight: 'var(--f-display-weight, 600)' as React.CSSProperties['fontWeight'],
          fontSize: 22,
          lineHeight: 1.1,
          letterSpacing: '-0.01em',
          color: INK,
        }}
      >
        {serviceName}
      </div>
      <div
        style={{
          marginTop: 10,
          fontSize: 13,
          color: INK_SOFT,
          letterSpacing: '-0.005em',
        }}
      >
        {formatDuration(duration)} · ${price.toFixed(2)}
        {tipAmount > 0 && ` · Tip $${tipAmount.toFixed(2)}`}
      </div>
      <div
        style={{
          marginTop: 14,
          paddingTop: 14,
          borderTop: `0.5px solid ${HAIRLINE}`,
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
        }}
      >
        <span style={{ fontSize: 12.5, color: INK_SOFT }}>Total</span>
        <span
          style={{
            fontFamily: 'var(--f-display, "Inter Tight", system-ui, sans-serif)',
            fontStyle: 'var(--f-display-style, normal)' as React.CSSProperties['fontStyle'],
            fontWeight: 'var(--f-display-weight, 600)' as React.CSSProperties['fontWeight'],
            fontSize: 20,
            color: INK,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          ${(price + tipAmount).toFixed(2)}
        </span>
      </div>
    </div>
  );
}

// ─── Composed view ───────────────────────────────────────────────

export type DetailsFieldBinding = {
  value: string;
  onChange?: (v: string) => void;
  onBlur?: () => void;
  error?: string;
};

export interface CustomerInfoPaymentViewProps {
  firstName: DetailsFieldBinding;
  lastName: DetailsFieldBinding;
  email: DetailsFieldBinding;
  phone: DetailsFieldBinding;
  specialRequests: DetailsFieldBinding;
  paymentMethod: string;
  onPaymentMethodChange?: (method: string) => void;
  cardPaymentsEnabled?: boolean;
  selectedService?: { name: string; duration: number; price: number } | null;
  tipAmount?: number;
  /** Production: TipSelection. Preview: omit. */
  tipSection?: React.ReactNode;
  /** Production: StripePaymentForm. Preview: omit. */
  paymentSection?: React.ReactNode;
  marketingChecked?: boolean;
  onMarketingChange?: (checked: boolean) => void;
  showMarketingCheckbox?: boolean;
  termsChecked?: boolean;
  onTermsChange?: (checked: boolean) => void;
  termsError?: string;
  cancellationPolicy?: string | null;
  turnstileSection?: React.ReactNode;
  footerNotice?: React.ReactNode;
  readOnly?: boolean;
  asForm?: boolean;
  formId?: string;
  ariaLabel?: string;
  onSubmit?: (e: React.FormEvent) => void;
}

export function CustomerInfoPaymentView({
  firstName,
  lastName,
  email,
  phone,
  specialRequests,
  paymentMethod,
  onPaymentMethodChange,
  cardPaymentsEnabled = true,
  selectedService,
  tipAmount = 0,
  tipSection,
  paymentSection,
  marketingChecked = false,
  onMarketingChange,
  showMarketingCheckbox = true,
  termsChecked = false,
  onTermsChange,
  termsError,
  cancellationPolicy,
  turnstileSection,
  footerNotice,
  readOnly = false,
  asForm = false,
  formId,
  ariaLabel,
  onSubmit,
}: CustomerInfoPaymentViewProps) {
  const showCardExtras =
    !!selectedService && !!paymentMethod && paymentMethod !== 'in_person';

  const body = (
    <>
      <SectionLabel>Your details</SectionLabel>
      <div
        style={{
          marginTop: 14,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 14,
        }}
      >
        <DetailsField
          name="firstName"
          label="First name"
          placeholder="Jane"
          maxLength={50}
          value={firstName.value}
          onChange={firstName.onChange}
          onBlur={firstName.onBlur}
          error={firstName.error}
          readOnly={readOnly}
        />
        <DetailsField
          name="lastName"
          label="Last name"
          placeholder="Doe"
          maxLength={50}
          value={lastName.value}
          onChange={lastName.onChange}
          onBlur={lastName.onBlur}
          error={lastName.error}
          readOnly={readOnly}
        />
      </div>
      <div style={{ marginTop: 14 }}>
        <DetailsField
          name="email"
          label="Email"
          placeholder="jane@email.com"
          type="email"
          inputMode="email"
          autoComplete="email"
          maxLength={100}
          value={email.value}
          onChange={email.onChange}
          onBlur={email.onBlur}
          error={email.error}
          readOnly={readOnly}
        />
      </div>
      <div style={{ marginTop: 14 }}>
        <DetailsField
          name="phone"
          label={
            <>
              Phone{' '}
              <span style={{ opacity: 0.7, textTransform: 'none', letterSpacing: '-0.005em' }}>
                (for booking reminders)
              </span>
            </>
          }
          placeholder="555-123-4567"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          maxLength={12}
          value={phone.value}
          onChange={phone.onChange}
          onBlur={phone.onBlur}
          error={phone.error}
          readOnly={readOnly}
        />
      </div>
      <div style={{ marginTop: 14 }}>
        <DetailsField
          name="specialRequests"
          label="Special requests (optional)"
          placeholder="Any allergies, preferences, or accessibility needs?"
          maxLength={500}
          multiline
          value={specialRequests.value}
          onChange={specialRequests.onChange}
          onBlur={specialRequests.onBlur}
          readOnly={readOnly}
        />
      </div>

      <div style={{ marginTop: 28 }}>
        <SectionLabel>Payment</SectionLabel>
        <div
          style={{
            marginTop: 12,
            display: 'grid',
            gridTemplateColumns: cardPaymentsEnabled ? '1fr 1fr' : '1fr',
            gap: 8,
          }}
        >
          {cardPaymentsEnabled && (
            <PaymentTile
              selected={paymentMethod === 'new_card'}
              onClick={() => onPaymentMethodChange?.('new_card')}
              label="Pay by card"
              sub="Secure checkout now"
              icon={<CardIcon />}
              readOnly={readOnly}
            />
          )}
          <PaymentTile
            selected={paymentMethod === 'in_person'}
            onClick={() => onPaymentMethodChange?.('in_person')}
            label="Pay in person"
            sub="Cash or card at visit"
            icon={<WalletIcon />}
            readOnly={readOnly}
          />
        </div>
      </div>

      {showCardExtras && selectedService && (
        <>
          {tipSection}
          {/*
            Production (main): price summary sat inside the tip extras gate.
            Preview (readOnly): show summary without tip/Stripe backends.
          */}
          {(tipSection || paymentSection || readOnly) && (
            <ServicePriceSummary
              serviceName={selectedService.name}
              duration={selectedService.duration}
              price={selectedService.price}
              tipAmount={tipAmount}
            />
          )}
          {paymentSection}
        </>
      )}

      <div
        style={{
          marginTop: 28,
          paddingTop: 18,
          borderTop: `0.5px solid ${HAIRLINE}`,
        }}
      >
        {showMarketingCheckbox && (
          <label
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              cursor: readOnly ? 'default' : 'pointer',
            }}
          >
            <input
              type="checkbox"
              name="marketingConsent"
              checked={!!marketingChecked}
              onChange={(e) => onMarketingChange?.(e.target.checked)}
              disabled={readOnly}
              style={{
                flexShrink: 0,
                width: 18,
                height: 18,
                marginTop: 2,
                accentColor: 'var(--f-accent, #1c160f)',
              }}
            />
            <span
              style={{
                fontSize: 11.5,
                lineHeight: 1.5,
                color: INK_SOFT,
                letterSpacing: '-0.005em',
              }}
            >
              I agree to receive recurring automated marketing text messages from Aleph Studios LLC
              d/b/a Figgy, including personalized product information, complimentary booking-page
              previews, and follow-ups about my interest in Figgy. Message and data rates may apply.
              Message frequency varies. Consent is not a condition of purchase. Reply HELP for help
              and STOP to cancel. View our{' '}
              <a
                href="/legal/terms-and-conditions"
                target="_blank"
                rel="noreferrer"
                onClick={(event) => event.stopPropagation()}
                style={{ color: 'inherit', textDecoration: 'underline' }}
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a
                href="/legal/privacy-policy"
                target="_blank"
                rel="noreferrer"
                onClick={(event) => event.stopPropagation()}
                style={{ color: 'inherit', textDecoration: 'underline' }}
              >
                Privacy Policy
              </a>
              . (Optional)
            </span>
          </label>
        )}

        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            marginTop: 14,
            cursor: readOnly ? 'default' : 'pointer',
          }}
        >
          <input
            type="checkbox"
            name="termsAccepted"
            checked={!!termsChecked}
            onChange={(e) => onTermsChange?.(e.target.checked)}
            disabled={readOnly}
            required={!readOnly}
            aria-invalid={!!termsError}
            style={{
              flexShrink: 0,
              width: 18,
              height: 18,
              marginTop: 2,
              accentColor: 'var(--f-accent, #1c160f)',
            }}
          />
          <span
            style={{
              fontSize: 11.5,
              lineHeight: 1.5,
              color: termsError ? 'rgba(200,60,40,0.9)' : INK_SOFT,
              letterSpacing: '-0.005em',
            }}
          >
            I agree to Figgy&apos;s{' '}
            <a
              href="/legal/terms-and-conditions"
              target="_blank"
              rel="noreferrer"
              onClick={(event) => event.stopPropagation()}
              style={{ color: 'inherit', textDecoration: 'underline' }}
            >
              Terms of Service
            </a>{' '}
            and acknowledge the{' '}
            <a
              href="/legal/privacy-policy"
              target="_blank"
              rel="noreferrer"
              onClick={(event) => event.stopPropagation()}
              style={{ color: 'inherit', textDecoration: 'underline' }}
            >
              Privacy Policy
            </a>
            .
            {termsError && (
              <span style={{ display: 'block', marginTop: 4 }}>{termsError}</span>
            )}
          </span>
        </label>

        <div style={{ marginTop: 16 }}>
          <PolicyCard policy={cancellationPolicy} />
        </div>
      </div>

      {turnstileSection}
      {footerNotice}
    </>
  );

  if (asForm) {
    return (
      <section>
        <form
          id={formId}
          onSubmit={onSubmit}
          aria-label={ariaLabel || 'Customer Information and Payment Form'}
        >
          {body}
        </form>
      </section>
    );
  }

  return <section>{body}</section>;
}